// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@eisinc/App/Components/Notify";
import { xCheckoutState } from "@eisinc/App/Store/Reducers";
import { getPageLinkByPageType } from "@eisinc/App/Store/Selectors/LinksSelectors";
import { doScroll } from "@eisinc/App/Utilities/helpers";
import { StepperContext } from "@eisinc/App/Widgets/CheckoutStepper/StepperContext";
import { shipmentDateTypeValues } from "@eisinc/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitCarrierService";
import { parseBoolean } from "@eisinc/XDL/Utilities";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage, { siteMessageWithCustomParserOptions } from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getBillToState, getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setFulfillmentMethodForCart from "@insite/client-framework/Store/Data/Carts/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouseForCart from "@insite/client-framework/Store/Data/Carts/Handlers/UpdatePickUpWarehouse";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import setPlaceOrderErrorMessage from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetPlaceOrderErrorMessage";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { DOMNode, domToReact, HTMLReactParserOptions } from "html-react-parser";
import React, { ChangeEvent, FC, useContext, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    setFulfillmentMethod,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart: makeHandlerChainAwaitable(updatePickUpWarehouseForCart),
    setPlaceOrderErrorMessage,
};

// XNG-Changes add xCheckoutState
const mapStateToProps = (state: ApplicationState & xCheckoutState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    const billTo = getCurrentBillToState(state).value;
    const shipTo = getCurrentShipToState(state).value;
    const customerSettings = getSettingsCollection(state).customerSettings;

    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    return {
        isLoading: cartState.isLoading,
        cart: cartState.value,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showPlaceOrderButton: canPlaceOrder(cartState.value),
        placeOrderErrorMessage: state.pages.checkoutReviewAndSubmit.placeOrderErrorMessage,
        showSubmitForApprovalOrder: canSubmitForApprovalOrder(cartState.value),
        hasProductsWithInvalidPrice: hasProductsWithInvalidPrice(cartState.value),
        canUpdateShipToAddress:
            billTo?.isGuest ||
            shipTo?.oneTimeAddress ||
            customerSettings.allowBillToAddressEdit ||
            customerSettings.allowShipToAddressEdit,
        updateCartInsteadOfContext: cartState.value?.isAwaitingApproval || !!cartId,
        // XNG-Changes
        billTo: getBillToState(state, cartState.value?.billToId).value,
        checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
        orderDeliveryBlock: state.xCheckout.orderDeliveryBlock,
        shipmentScheduleFromState: state.xCheckout.shipmentSchedule,
        isPickUp: state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp,
        customerType: getSession(state).properties?.["mpExistingCustomer"],
    };
};

// XNG-Changes : add HasHistory
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutReviewAndSubmitCartTotalStyles {
    container?: GridContainerProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    buttonsGridItem?: GridItemProps;
    hasInsufficientInventoryGridItem?: GridItemProps;
    availabilityErrorMessageText?: TypographyPresentationProps;
    placeOrderErrorMessageGridItem?: GridItemProps;
    placeOrderErrorMessageText?: TypographyPresentationProps;
    invalidEditableAddressErrorMessageGridItem?: GridItemProps;
    invalidEditableAddressErrorMessageText?: TypographyPresentationProps;
    invalidAddressErrorMessageGridItem?: GridItemProps;
    invalidAddressErrorMessageText?: TypographyPresentationProps;
    invalidPriceErrorMessageGridItem?: GridItemProps;
    invalidPriceErrorMessageText?: TypographyPresentationProps;
    placeOrderButton?: ButtonPresentationProps;
    submitForApprovalButton?: ButtonPresentationProps;
    findLocationModal?: FindLocationModalStyles;
    defaultFulfillmentMethodRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    defaultFulfillmentMethodRadio?: FieldSetPresentationProps<RadioComponentProps>;
}

export const cartTotalStyles: CheckoutReviewAndSubmitCartTotalStyles = {
    container: { gap: 10 },
    cartTotalGridItem: { width: 12 },
    buttonsGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
            flex-direction: column;
        `,
    },
    hasInsufficientInventoryGridItem: { width: 12 },
    availabilityErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    placeOrderErrorMessageGridItem: { width: 12 },
    placeOrderErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    invalidEditableAddressErrorMessageGridItem: { width: 12 },
    invalidEditableAddressErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    invalidAddressErrorMessageGridItem: { width: 12 },
    invalidAddressErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    invalidPriceErrorMessageGridItem: { width: 12 },
    invalidPriceErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            margin-bottom: 15px;
        `,
    },
    placeOrderButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    submitForApprovalButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    defaultFulfillmentMethodRadioGroup: {
        css: css`
            display: block;
            ${RadioStyle} {
                display: inline-block;
                margin-right: 1em;
                & + ${RadioStyle} {
                    margin-top: 0;
                }
            }
        `,
    },
};

const styles = cartTotalStyles;

const CheckoutReviewAndSubmitCartTotal: FC<Props> = ({
    showPlaceOrderButton,
    cart,
    isLoading,
    shippingPromotions,
    discountTotal,
    orderPromotions,
    placeOrderErrorMessage,
    updatePickUpWarehouse,
    setFulfillmentMethod,
    showSubmitForApprovalOrder,
    canUpdateShipToAddress,
    hasProductsWithInvalidPrice,
    updateCartInsteadOfContext,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart,
    // XNG-Changes
    billTo,
    history,
    checkoutShippingPage,
    orderDeliveryBlock,
    shipmentScheduleFromState,
    isPickUp,
    setPlaceOrderErrorMessage,
    customerType,
}) => {
    // <XNG-Changes>
    const { currentStep, setCurrentStep } = useContext(StepperContext);
    // </XNG-Changes>

    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    // <XNG-Changes>
    const [isWarehouseSelectedLoading, setIsWarehouseSelectedLoading] = React.useState(false);

    // XNG-Change: disable next button when there is no scheduled dates
    const requiredFieldsNotFilled = useMemo(() => {
        if (
            currentStep === 2 &&
            !isPickUp &&
            shipmentScheduleFromState === shipmentDateTypeValues.ScheduledDates &&
            !parseBoolean(cart?.properties?.hasScheduledDeliveries)
        ) {
            return true;
        }
        return false;
    }, [currentStep, cart?.properties, shipmentScheduleFromState, isPickUp]);
    // </XNG-Changes>

    const openWarehouseSelectionModal = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);

    const toasterContext = React.useContext(ToasterContext);

    // <XNG-Changes>
    const updatePickUpSelectedWarehouse = async (pickUpWarehouse: WarehouseModel) => {
        if (updateCartInsteadOfContext && cart) {
            await updatePickUpWarehouseForCart({ cart, pickUpWarehouse });
        } else {
            await updatePickUpWarehouse({ pickUpWarehouse });
        }
    };
    // </XNG-Changes>

    // XNG-Changes: remove async
    const handleWarehouseSelected = (pickUpWarehouse: WarehouseModel) => {
        // XNG-Changes
        // await updatePickUpWarehouse({ pickUpWarehouse });
        setIsWarehouseSelectedLoading(true);
        updatePickUpSelectedWarehouse(pickUpWarehouse).then(result => {
            toasterContext.addToast({
                body: translate("Pick Up Address Updated"),
                messageType: "success",
            });
            setIsFindLocationOpen(false);
            // XNG-Changes
            setIsWarehouseSelectedLoading(false);
        });
    };

    const [isFulfillmentMethodOpen, setIsFulfillmentMethodOpen] = React.useState(false);
    const openDeliveryMethodPopup = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsFulfillmentMethodOpen(true);
    };

    const modalCloseHandler = () => {
        setIsFulfillmentMethodOpen(false);
    };

    const fulfillmentMethodChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (updateCartInsteadOfContext && cart) {
            setFulfillmentMethodForCart({
                cart,
                fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp",
            });
        } else {
            setFulfillmentMethod({ fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp" });
        }
    };

    const parserOptions: HTMLReactParserOptions = {
        replace: node => {
            if (!("name" in node && "children" in node)) {
                return;
            }
            const { name, children, attribs = { style: "" } } = node;
            const { style, class: className, ...otherAttribs } = attribs;

            otherAttribs.className = className;

            if (name === "a") {
                let onClickHandler: ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined;
                for (const key in otherAttribs) {
                    if (key === "ng-click") {
                        if (otherAttribs[key] === "vm.openWarehouseSelectionModal()") {
                            onClickHandler = openWarehouseSelectionModal;
                        }
                        if (otherAttribs[key] === "vm.openDeliveryMethodPopup()") {
                            onClickHandler = openDeliveryMethodPopup;
                        }
                    }
                }
                return (
                    <Link
                        css={style as any}
                        id={otherAttribs.name}
                        {...otherAttribs}
                        onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
                            onClickHandler && onClickHandler(event)
                        }
                    >
                        {children && domToReact(children as DOMNode[], parserOptions)}
                    </Link>
                );
            }
        },
    };
    // <XNG-Changes>

    const goNext = () => {
        setCurrentStep(currentStep + 1);
        if (window.innerWidth <= 768) {
            doScroll(true);
        } else {
            doScroll();
        }
    };

    const goBack = () => {
        if (currentStep === 2) {
            history.push(`${checkoutShippingPage?.url as string}?cartId=${cart?.id}`);
        } else {
            if (currentStep === 4) {
                setPlaceOrderErrorMessage({});
            }
            setCurrentStep(currentStep - 1);
        }
        if (window.innerWidth <= 768) {
            window.scrollTo(0, 0);
        }
    };

    React.useEffect(() => {
        (window as any)._GaCheckoutFunnel && (window as any)._GaCheckoutFunnel(cart, currentStep);
        (window as any)._MpCheckoutFunnel && (window as any)._MpCheckoutFunnel(currentStep, customerType);
    }, [currentStep]);

    const showQuickFabWarning = () => {
        const QuickFabCheck = cart?.cartLines?.some(cartLine => cartLine.properties.isQuickShip === "true");

        if (cart?.properties?.quickShipWarning === "true" || (QuickFabCheck && orderDeliveryBlock === "true")) {
            return true;
        }
        return false;
    };

    // </XNG-Changes>

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.cartTotalGridItem}>
                    <CartTotalDisplay
                        cart={cart}
                        isLoading={isLoading}
                        shippingPromotions={shippingPromotions}
                        discountTotal={discountTotal}
                        orderPromotions={orderPromotions}
                        {...({ showCartUrl: true } as any)}
                    />
                </GridItem>

                <GridItem {...styles.buttonsGridItem}>
                    <Button variant="secondary" onClick={goBack} disabled={currentStep < 0 || isLoading}>
                        {translate("Back")}
                    </Button>
                    {currentStep !== 4 && (
                        <Button
                            variant="primary"
                            onClick={goNext}
                            disabled={currentStep < 0 || isLoading || requiredFieldsNotFilled}
                        >
                            {translate("Next")}
                        </Button>
                    )}
                    {cart && showPlaceOrderButton && currentStep === 4 && (
                        <PlaceOrderButton styles={styles.placeOrderButton} />
                    )}
                    {cart && showSubmitForApprovalOrder && currentStep === 4 && (
                        <SubmitForApprovalButton extendedStyles={styles.submitForApprovalButton} />
                    )}
                </GridItem>
                {cart && cart.hasInsufficientInventory === true && (
                    <GridItem {...styles.hasInsufficientInventoryGridItem}>
                        <Typography
                            {...styles.availabilityErrorMessageText}
                            data-test-selector="checkoutReviewAndSubmit_availabilityErrorMessage"
                        >
                            {siteMessageWithCustomParserOptions(
                                "ReviewAndPay_NotEnoughInventoryForPickup",
                                parserOptions,
                            )}
                        </Typography>
                        <FindLocationModal
                            modalIsOpen={isFindLocationOpen}
                            onWarehouseSelected={handleWarehouseSelected}
                            onModalClose={handleFindLocationModalClose}
                            forCart
                            extendedStyles={styles.findLocationModal}
                            {...({ isWarehouseSelectedLoading } as any)}
                        />
                        <Modal
                            size={500}
                            headline={translate("Fulfillment Method")}
                            isOpen={isFulfillmentMethodOpen}
                            handleClose={modalCloseHandler}
                        >
                            <RadioGroup
                                value={cart.fulfillmentMethod}
                                onChangeHandler={fulfillmentMethodChangeHandler}
                                {...styles.defaultFulfillmentMethodRadioGroup}
                            >
                                <Radio value={FulfillmentMethod.Ship} {...styles.defaultFulfillmentMethodRadio}>
                                    {translate("Ship")}
                                </Radio>
                                <Radio value={FulfillmentMethod.PickUp} {...styles.defaultFulfillmentMethodRadio}>
                                    {translate("Pick Up")}
                                </Radio>
                            </RadioGroup>
                        </Modal>
                    </GridItem>
                )}
                {placeOrderErrorMessage && (
                    <GridItem {...styles.placeOrderErrorMessageGridItem}>
                        <Notify
                            messageType="danger"
                            body={placeOrderErrorMessage}
                            data-test-selector="checkoutReviewAndSubmit_placeOrderErrorMessage"
                            css={css`
                                margin-top: 15px;
                                margin-inline: 12px;
                            `}
                        />
                    </GridItem>
                )}
                {cart?.properties["orderLessThanMinimumOrderValue"] === "true" && cart?.totalCountDisplay > 0 && (
                    <GridItem {...styles.placeOrderErrorMessageGridItem}>
                        <Notify
                            messageType="warning"
                            showCloseButton={true}
                            body={
                                /* eslint-disable react/no-danger */
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: siteMessage("Cart_LessThanOrderThreshold") as any,
                                    }}
                                ></div>
                            }
                        />
                    </GridItem>
                )}
                {cart?.taxFailureReason === "InvalidAddress" && canUpdateShipToAddress && (
                    <GridItem {...styles.invalidEditableAddressErrorMessageGridItem}>
                        <Notify
                            messageType="danger"
                            body={siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax_UpdateAddress")}
                            css={css`
                                margin-top: 15px;
                                margin-bottom: 0;
                            `}
                        />
                    </GridItem>
                )}
                {cart?.taxFailureReason === "InvalidAddress" && !canUpdateShipToAddress && (
                    <GridItem {...styles.invalidAddressErrorMessageGridItem}>
                        <Notify
                            messageType="danger"
                            body={siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax")}
                            css={css`
                                margin-top: 15px;
                                margin-bottom: 0;
                            `}
                        />
                    </GridItem>
                )}
                {hasProductsWithInvalidPrice && (
                    <GridItem {...styles.invalidPriceErrorMessageGridItem}>
                        <Typography {...styles.invalidPriceErrorMessageText}>
                            {siteMessage("ReviewAndPay_HasProductsWithInvalidPrice")}
                        </Typography>
                    </GridItem>
                )}
            </GridContainer>

            {parseBoolean(cart?.properties?.calculatingTaxFailed) && (
                <Notify
                    messageType="info"
                    body={siteMessage("Custom_Tax_Order_Simulation_Issue")}
                    css={css`
                        margin-top: 15px;
                        margin-bottom: 0;
                    `}
                />
            )}

            {parseBoolean(billTo?.properties?.taxExemptionInProgress) && (
                <Notify
                    messageType="info"
                    body={siteMessage("Custom_Tax_Flagged_Submitting")}
                    css={css`
                        margin-top: 15px;
                        margin-bottom: 0;
                    `}
                />
            )}

            {/* Xng-change: EIS-3697: show quickfab warning for requesting certification OR complete/whole order delivery */}
            {showQuickFabWarning() && (
                <Notify
                    messageType="info"
                    body={siteMessage(
                        "Custom_QuickFab_Checkout_warning",
                        "There are items in you cart potentially hold up your order",
                    )}
                    css={css`
                        margin-top: 15px;
                        margin-bottom: 0;
                    `}
                />
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutReviewAndSubmitCartTotal)),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Cart Total",
    },
};

export default widgetModule;
